import { useState, useEffect } from "react";
import { MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";

interface Activity {
  id: string;
  name: string;
  city: string;
  action: string;
  timestamp: Date;
  messageId?: string; // Database ID for tracking
}

export type ActivityFeedContext = 'general' | 'trainers' | 'gyms' | 'events' | 'tribus' | 'professionals';

interface LiveActivityFeedProps {
  context?: ActivityFeedContext;
}

const GENERAL_ACTIVITIES: Activity[] = [
  { id: "1", name: "Juan", city: "Madrid", action: "se registró en la plataforma", timestamp: new Date() },
  { id: "2", name: "María", city: "Barcelona", action: "actualizó a Premium", timestamp: new Date() },
  { id: "3", name: "Carlos", city: "Valencia", action: "creó su perfil de entrenador", timestamp: new Date() },
  { id: "4", name: "Ana", city: "Sevilla", action: "se unió a la comunidad", timestamp: new Date() },
  { id: "5", name: "Pedro", city: "Bilbao", action: "activó cupones de descuento", timestamp: new Date() },
  { id: "6", name: "Laura", city: "Málaga", action: "se registró como nuevo usuario", timestamp: new Date() },
  { id: "7", name: "Diego", city: "Zaragoza", action: "creó una tribu de running", timestamp: new Date() },
  { id: "8", name: "Sofía", city: "Alicante", action: "completó su registro", timestamp: new Date() },
  { id: "9", name: "Miguel", city: "Murcia", action: "recibió 3 nuevos leads", timestamp: new Date() },
  { id: "10", name: "Carmen", city: "Palma", action: "se unió a la plataforma", timestamp: new Date() },
  { id: "11", name: "Javier", city: "Las Palmas", action: "publicó un cupón de bienvenida", timestamp: new Date() },
  { id: "12", name: "Isabel", city: "Bilbao", action: "creó su cuenta", timestamp: new Date() },
  { id: "13", name: "Roberto", city: "Valladolid", action: "actualizó a plan Premium", timestamp: new Date() },
  { id: "14", name: "Elena", city: "Vigo", action: "se registró como nutricionista", timestamp: new Date() },
  { id: "15", name: "Antonio", city: "Gijón", action: "completó su perfil", timestamp: new Date() },
];

const TRAINER_ACTIVITIES: Activity[] = [
  { id: "t1", name: "Carlos", city: "Madrid", action: "activó perfil premium de entrenador", timestamp: new Date() },
  { id: "t2", name: "Natalia", city: "Barcelona", action: "subió video de transformación cliente", timestamp: new Date() },
  { id: "t3", name: "Fernando", city: "Valencia", action: "recibió 7 nuevos clientes", timestamp: new Date() },
  { id: "t4", name: "Sofía", city: "Sevilla", action: "completó certificación fitness", timestamp: new Date() },
  { id: "t5", name: "Álvaro", city: "Málaga", action: "creó plan personalizado online", timestamp: new Date() },
  { id: "t6", name: "Victoria", city: "Bilbao", action: "ganó badge entrenador verificado", timestamp: new Date() },
  { id: "t7", name: "Pablo", city: "Zaragoza", action: "alcanzó 50 clientes activos", timestamp: new Date() },
  { id: "t8", name: "Cristina", city: "Alicante", action: "ofreció primera sesión gratis", timestamp: new Date() },
  { id: "t9", name: "Rubén", city: "Murcia", action: "especializó en entrenamiento funcional", timestamp: new Date() },
  { id: "t10", name: "Laura", city: "Palma", action: "subió rutinas de hipertrofia", timestamp: new Date() },
];

const GYM_ACTIVITIES: Activity[] = [
  { id: "g1", name: "Juan", city: "Madrid", action: "abrió nuevo centro de CrossFit", timestamp: new Date() },
  { id: "g2", name: "María", city: "Barcelona", action: "renovó equipamiento del gimnasio", timestamp: new Date() },
  { id: "g3", name: "Roberto", city: "Valencia", action: "lanzó clases grupales ilimitadas", timestamp: new Date() },
  { id: "g4", name: "Isabel", city: "Sevilla", action: "activó cupón 50% primer mes", timestamp: new Date() },
  { id: "g5", name: "Pedro", city: "Málaga", action: "instaló nueva zona funcional", timestamp: new Date() },
  { id: "g6", name: "Lucía", city: "Bilbao", action: "sumó clases de yoga y pilates", timestamp: new Date() },
  { id: "g7", name: "Javier", city: "Zaragoza", action: "alcanzó 200 miembros activos", timestamp: new Date() },
  { id: "g8", name: "Ana", city: "Alicante", action: "inauguró zona de piscina", timestamp: new Date() },
  { id: "g9", name: "Diego", city: "Murcia", action: "ofreció clases 24/7", timestamp: new Date() },
  { id: "g10", name: "Carmen", city: "Palma", action: "recibió certificación sanitaria", timestamp: new Date() },
];

const EVENT_ACTIVITIES: Activity[] = [
  { id: "e1", name: "Ana", city: "Madrid", action: "organizó maratón urbano 10K", timestamp: new Date() },
  { id: "e2", name: "Álvaro", city: "Barcelona", action: "publicó evento triatlón olimpico", timestamp: new Date() },
  { id: "e3", name: "Isabel", city: "Valencia", action: "creó torneo padel mixto", timestamp: new Date() },
  { id: "e4", name: "Miguel", city: "Sevilla", action: "lanzó bootcamp al aire libre", timestamp: new Date() },
  { id: "e5", name: "Patricia", city: "Málaga", action: "organizó yoga sunset en playa", timestamp: new Date() },
  { id: "e6", name: "Sergio", city: "Bilbao", action: "publicó carrera montaña extrema", timestamp: new Date() },
  { id: "e7", name: "Elena", city: "Zaragoza", action: "creó seminario nutrición deportiva", timestamp: new Date() },
  { id: "e8", name: "Daniel", city: "Alicante", action: "organizó competición CrossFit", timestamp: new Date() },
  { id: "e9", name: "Rosa", city: "Murcia", action: "lanzó evento ciclismo ruta", timestamp: new Date() },
  { id: "e10", name: "Antonio", city: "Palma", action: "publicó masterclass funcional", timestamp: new Date() },
];

const TRIBU_ACTIVITIES: Activity[] = [
  { id: "tr1", name: "Laura", city: "Madrid", action: "creó tribu de running matinal", timestamp: new Date() },
  { id: "tr2", name: "Diego", city: "Barcelona", action: "se unió a tribu CrossFit hardcore", timestamp: new Date() },
  { id: "tr3", name: "Cristina", city: "Valencia", action: "fundó comunidad yoga urbano", timestamp: new Date() },
  { id: "tr4", name: "Pablo", city: "Sevilla", action: "organizó quedada ciclismo fin de semana", timestamp: new Date() },
  { id: "tr5", name: "Marta", city: "Málaga", action: "creó tribu natación aguas abiertas", timestamp: new Date() },
  { id: "tr6", name: "Sergio", city: "Bilbao", action: "se sumó a tribu boxeo amateur", timestamp: new Date() },
  { id: "tr7", name: "Raquel", city: "Zaragoza", action: "lanzó grupo escalada indoor", timestamp: new Date() },
  { id: "tr8", name: "Iván", city: "Alicante", action: "creó tribu calistenia parque", timestamp: new Date() },
  { id: "tr9", name: "Beatriz", city: "Murcia", action: "organizó tribu pilates reformer", timestamp: new Date() },
  { id: "tr10", name: "Jorge", city: "Palma", action: "fundó comunidad paddle surf", timestamp: new Date() },
];

const PROFESSIONAL_ACTIVITIES: Activity[] = [
  { id: "p1", name: "Elena", city: "Madrid", action: "registró su perfil de nutricionista", timestamp: new Date() },
  { id: "p2", name: "Antonio", city: "Barcelona", action: "activó consulta online fisioterapia", timestamp: new Date() },
  { id: "p3", name: "Carmen", city: "Valencia", action: "ofreció plan nutricional personalizado", timestamp: new Date() },
  { id: "p4", name: "Fernando", city: "Sevilla", action: "sumó terapia deportiva premium", timestamp: new Date() },
  { id: "p5", name: "Victoria", city: "Málaga", action: "creó programa pérdida peso", timestamp: new Date() },
  { id: "p6", name: "Rubén", city: "Bilbao", action: "publicó servicios recuperación lesiones", timestamp: new Date() },
  { id: "p7", name: "Silvia", city: "Zaragoza", action: "lanzó asesoría nutricional deportiva", timestamp: new Date() },
  { id: "p8", name: "Óscar", city: "Alicante", action: "activó consultas fisio a domicilio", timestamp: new Date() },
  { id: "p9", name: "Noelia", city: "Murcia", action: "ofreció planes detox y wellness", timestamp: new Date() },
  { id: "p10", name: "Manuel", city: "Palma", action: "creó programa rehabilitación integral", timestamp: new Date() },
];

const getActivitiesByContext = (context: ActivityFeedContext): Activity[] => {
  switch (context) {
    case 'trainers':
      return TRAINER_ACTIVITIES;
    case 'gyms':
      return GYM_ACTIVITIES;
    case 'events':
      return EVENT_ACTIVITIES;
    case 'tribus':
      return TRIBU_ACTIVITIES;
    case 'professionals':
      return PROFESSIONAL_ACTIVITIES;
    case 'general':
    default:
      return GENERAL_ACTIVITIES;
  }
};

export const LiveActivityFeed = ({ context = 'general' }: LiveActivityFeedProps) => {
  const location = useLocation();
  const [currentActivity, setCurrentActivity] = useState<Activity | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [sessionId] = useState(() => crypto.randomUUID());

  // Fetch activities from database
  useEffect(() => {
    const fetchActivities = async () => {
      const { data, error } = await supabase
        .from('activity_feed_messages')
        .select('*')
        .eq('context', context)
        .eq('is_active', true)
        .order('priority', { ascending: false })
        .limit(20);

      if (data && !error) {
        const dbActivities: Activity[] = data.map(msg => ({
          id: msg.id,
          messageId: msg.id,
          name: msg.name,
          city: msg.city,
          action: msg.action,
          timestamp: new Date()
        }));
        setActivities(dbActivities);
      } else {
        // Fallback to static activities if DB fails
        setActivities(getActivitiesByContext(context));
      }
    };

    fetchActivities();
  }, [context]);

  useEffect(() => {
    // Subscribe to new listings
    if (context === 'general' || context === 'gyms') {
      const listingsChannel = supabase
        .channel("listings-feed")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "listings"
          },
          (payload) => {
            const listing = payload.new as any;
            const newActivity: Activity = {
              id: listing.id,
              name: listing.name?.split(" ")[0] || "Alguien",
              city: listing.city || "España",
              action: listing.is_premium ? "activó Premium" : "creó su listado",
              timestamp: new Date()
            };
            setActivities(prev => [newActivity, ...prev].slice(0, 15));
          }
        )
        .subscribe();

      return () => {
        listingsChannel.unsubscribe();
      };
    }
  }, [context]);

  // Subscribe to new user signups
  useEffect(() => {
    const cities = ["Madrid", "Barcelona", "Valencia", "Sevilla", "Málaga", "Bilbao", "Zaragoza", "Alicante", "Murcia", "Palma"];
    
    const signupChannel = supabase
      .channel("profile-signups")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "profiles"
        },
        (payload) => {
          const profile = payload.new as any;
          const firstName = profile.full_name?.split(" ")[0] || "Alguien";
          const randomCity = cities[Math.floor(Math.random() * cities.length)];
          
          const newActivity: Activity = {
            id: `signup-${profile.id}`,
            name: firstName,
            city: randomCity,
            action: "se unió a la comunidad",
            timestamp: new Date()
          };
          setActivities(prev => [newActivity, ...prev].slice(0, 15));
        }
      )
      .subscribe();

    return () => {
      signupChannel.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Show activity every 30-45 seconds (reduced frequency)
    const showActivity = async () => {
      if (activities.length === 0) return;
      
      const randomActivity = activities[Math.floor(Math.random() * activities.length)];
      setCurrentActivity(randomActivity);
      setIsVisible(true);

      // Track impression
      if (randomActivity.messageId) {
        supabase.rpc('increment_activity_impressions', { 
          message_id: randomActivity.messageId 
        }).then(() => {});
      }

      // Hide after 4 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);
    };

    // Initial delay of 10 seconds
    const initialDelay = setTimeout(showActivity, 10000);
    
    // Repeat every 30-45 seconds
    const interval = setInterval(showActivity, Math.random() * 15000 + 30000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [activities]);

  const handleActivityClick = async () => {
    if (!currentActivity?.messageId) return;

    // Track click
    await Promise.all([
      supabase.from('activity_feed_clicks').insert({
        message_id: currentActivity.messageId,
        session_id: sessionId,
        page_path: location.pathname,
        context: context,
        metadata: { name: currentActivity.name, city: currentActivity.city }
      }),
      supabase.rpc('increment_activity_clicks', {
        message_id: currentActivity.messageId
      })
    ]);
  };

  if (!currentActivity) return null;

  return (
    <div
      className={`fixed bottom-6 left-6 z-50 transition-all duration-500 transform cursor-pointer ${
        isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
      }`}
      onClick={handleActivityClick}
    >
      <div className="bg-card border border-border rounded-lg shadow-lg p-4 max-w-sm animate-fade-in">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold flex-shrink-0">
            {currentActivity.name.charAt(0)}
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              <span className="font-bold">{currentActivity.name}</span> {currentActivity.action}
            </p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />
              {currentActivity.city}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Hace unos minutos</p>
          </div>

          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0 mt-2" />
        </div>
      </div>
    </div>
  );
};
